import { ReactNode, useEffect, useRef, useState } from 'react';
import { loadPdfJs } from '@fias/pdf-core/loader';

interface PdfPageViewProps {
  /** The fully-loaded pdfjs document handle (one per source PDF). */
  pdfDoc: import('pdfjs-dist').PDFDocumentProxy | null;
  /** 1-indexed page number to render. */
  pageNumber: number;
  /** Width in CSS pixels at which to render the page. Height is derived from
   *  the page's intrinsic aspect ratio. */
  width: number;
  /** Children rendered in a positioned overlay above the canvas, used for
   *  field placement. The overlay tracks the canvas's rendered size 1:1. */
  children?: (renderedSize: { width: number; height: number }) => ReactNode;
}

/**
 * Renders one PDF page to a canvas — lazily, via IntersectionObserver. A
 * 200-page document would be unusable if every page rendered eagerly, so we
 * defer the heavy `page.render()` work until the page scrolls within
 * `rootMargin` of the viewport.
 *
 * Re-renders happen if `width` changes (responsive resize) or `pageNumber`
 * changes. Each render cancels the previous in-flight one so a fast scroll
 * doesn't queue dozens of pending render tasks.
 */
export function PdfPageView({ pdfDoc, pageNumber, width, children }: PdfPageViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [visible, setVisible] = useState(false);
  const [renderedSize, setRenderedSize] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
            return;
          }
        }
      },
      { rootMargin: '200px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !pdfDoc || !canvasRef.current) return;

    let cancelled = false;
    let renderTask: import('pdfjs-dist').RenderTask | undefined;

    (async () => {
      const page = await pdfDoc.getPage(pageNumber);
      if (cancelled) return;

      const baseViewport = page.getViewport({ scale: 1 });
      const scale = width / baseViewport.width;
      const viewport = page.getViewport({ scale });

      const canvas = canvasRef.current;
      if (!canvas) return;

      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      renderTask = page.render({ canvasContext: ctx, viewport, canvas });
      try {
        await renderTask.promise;
        if (!cancelled) {
          setRenderedSize({ width: viewport.width, height: viewport.height });
        }
      } catch (err) {
        // pdfjs throws "RenderingCancelledException" when we cancel; that's
        // expected and not worth surfacing.
        if (!cancelled && err instanceof Error && err.name !== 'RenderingCancelledException') {
          console.warn(`Failed to render PDF page ${pageNumber}`, err);
        }
      }
    })();

    return () => {
      cancelled = true;
      try {
        renderTask?.cancel();
      } catch {
        // already settled
      }
    };
  }, [visible, pdfDoc, pageNumber, width]);

  return (
    <div
      ref={containerRef}
      className="relative inline-block bg-muted"
      style={{ width, minHeight: width * 1.3 }}
      data-testid={`pdf-page-${pageNumber}`}
    >
      <canvas ref={canvasRef} className="block" />
      {renderedSize && children ? (
        <div
          className="absolute inset-0"
          style={{ width: renderedSize.width, height: renderedSize.height }}
        >
          {children(renderedSize)}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Loads a pdfjs document from a URL and tracks its lifecycle. Returns null
 * while loading or if no URL is set; error surfaces via thrown promise so
 * callers can show their own UI state.
 */
export function usePdfDocument(url: string | null) {
  const [doc, setDoc] = useState<import('pdfjs-dist').PDFDocumentProxy | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!url) {
      setDoc(null);
      return;
    }
    let cancelled = false;
    let loadingTask: import('pdfjs-dist').PDFDocumentLoadingTask | undefined;
    (async () => {
      try {
        const pdfjs = await loadPdfJs();
        loadingTask = pdfjs.getDocument({ url });
        const loaded = await loadingTask.promise;
        if (cancelled) {
          loaded.destroy();
          return;
        }
        setDoc(loaded);
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load PDF');
        }
      }
    })();
    return () => {
      cancelled = true;
      if (loadingTask) {
        loadingTask.destroy().catch(() => {
          // already destroyed
        });
      }
    };
  }, [url]);

  return { doc, error };
}
